import PropTypes from 'prop-types';
import { useState } from 'react';
import { Box, IconButton, Popover, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import Iconify from 'src/components/Iconify';
import { primary } from 'src/theme/palette';
import { TASK_LAYOUT_MODES } from './useTaskLayoutMode';

// -----------------------------------------------
// TaskLayoutSwitcher — small icon-button + popover with three tiles for
// switching the current task between Modal / Full screen / Sidebar layouts.
// Mirrors the ClickUp affordance shown in the reference screenshot.
//
// Props:
//   mode      — current layout mode ('sidebar' | 'modal' | 'full')
//   onChange  — (nextMode) => void; called on tile click
//   compact   — if true, renders only the icon (default true). Used in the
//               narrow slideout/modal headers; the full-page header passes
//               compact={false} to show a slightly larger affordance.
// -----------------------------------------------

const MODES = [
    {
        value: TASK_LAYOUT_MODES.MODAL,
        label: 'Modal',
        icon: 'eva:square-outline',
        description: 'Center of screen',
    },
    {
        value: TASK_LAYOUT_MODES.FULL,
        label: 'Full screen',
        icon: 'eva:expand-outline',
        description: 'Take over the page',
    },
    {
        value: TASK_LAYOUT_MODES.SIDEBAR,
        label: 'Sidebar',
        icon: 'eva:menu-2-outline',
        description: 'Right-side drawer',
    },
];

export default function TaskLayoutSwitcher({ mode, onChange, compact }) {
    const [anchor, setAnchor] = useState(null);
    const open = Boolean(anchor);

    const handleSelect = (next) => {
        setAnchor(null);
        if (next !== mode) onChange?.(next);
    };

    const activeIcon = (MODES.find((m) => m.value === mode) || MODES[2]).icon;

    return (
        <>
            <Tooltip title="Switch layout">
                <IconButton
                    size="small"
                    aria-label="Switch task layout"
                    onClick={(e) => setAnchor(e.currentTarget)}
                    sx={{
                        color: 'text.secondary',
                        '&:hover': { color: primary.main, bgcolor: alpha(primary.main, 0.08) },
                        ...(compact ? {} : { width: 32, height: 32 }),
                    }}
                >
                    <Iconify icon={activeIcon} width={compact ? 18 : 20} />
                </IconButton>
            </Tooltip>
            <Popover
                open={open}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { mt: 0.5, p: 1, borderRadius: 2, boxShadow: '0 4px 16px rgba(0,0,0,0.10)' } }}
            >
                <Box sx={{ display: 'flex', gap: 0.75, p: 0.5 }}>
                    {MODES.map((m) => {
                        const selected = m.value === mode;
                        return (
                            <Box
                                key={m.value}
                                role="button"
                                tabIndex={0}
                                onClick={() => handleSelect(m.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSelect(m.value); }}
                                sx={{
                                    width: 96,
                                    p: 1.25,
                                    borderRadius: 1.5,
                                    cursor: 'pointer',
                                    border: '1px solid',
                                    borderColor: selected ? primary.main : 'divider',
                                    bgcolor: selected ? alpha(primary.main, 0.08) : 'transparent',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    textAlign: 'center',
                                    transition: 'all 0.15s ease',
                                    '&:hover': {
                                        bgcolor: alpha(primary.main, 0.06),
                                        borderColor: primary.main,
                                    },
                                    '&:focus-visible': { outline: `2px solid ${primary.main}`, outlineOffset: 2 },
                                }}
                            >
                                <Iconify
                                    icon={m.icon}
                                    width={22}
                                    sx={{ color: selected ? primary.main : 'text.secondary' }}
                                />
                                <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: selected ? primary.main : 'text.primary' }}>
                                    {m.label}
                                </Typography>
                                <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', lineHeight: 1.2 }}>
                                    {m.description}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Popover>
        </>
    );
}

TaskLayoutSwitcher.propTypes = {
    mode: PropTypes.oneOf(['sidebar', 'modal', 'full']).isRequired,
    onChange: PropTypes.func.isRequired,
    compact: PropTypes.bool,
};

TaskLayoutSwitcher.defaultProps = {
    compact: true,
};
